// ═══════════════════════════════════════════════════════════════════════════
//  ExamenNivelPage — el alumno presenta un examen de nivel (Fase 3)
//
//  Diseño estándar de NSL (FLUENCY NEXUS): neumórfico, con auras, entradas con
//  rebote y un cierre con efecto wow (anillo de puntaje animado). Tres pantallas:
//  LISTA · CORRIENDO · RESULTADO — cada una con su navegación de vuelta.
//
//  ⚠️  Sin feedback de acierto durante el examen (igual que el de colocación):
//      revelar la respuesta contamina la medición. El resultado va al final.
// ═══════════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ItemRenderer from '../components/assessment/ItemRenderer';
import { cancelSpeech } from '../utils/browserSpeech';
import { listExams, startExam, answerExam, examResult } from '../components/assessment/examsApi';
import '../components/assessment/assessment.css';
import './ExamenNivelPage.css';

const SKILL_ICON = {
  use_of_english: '🧩', reading: '📖', listening: '🎧', speaking: '🗣️', writing: '✍️',
};

export default function ExamenNivelPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const codeDirecto = params.get('code');
  const resultDirecto = params.get('result');   // ver el resultado de un intento pasado
  const [fase, setFase] = useState('lista');          // 'lista' | 'corriendo' | 'resultado'
  const [exams, setExams] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [sesion, setSesion] = useState(null);
  const [resultado, setResultado] = useState(null);

  const empezar = useCallback(async (exam) => {
    setError(null);
    try {
      const d = await startExam(exam.code);
      if (d.done) { setResultado(d.result); setFase('resultado'); return; }
      setSesion({ sessionId: d.session_id, item: d.item, progress: d.progress, examTitle: exam.title });
      setFase('corriendo');
    } catch (e) { setError(e.message); }
  }, []);

  useEffect(() => {
    let vivo = true;
    // Ver el resultado de un intento pasado (desde el dashboard) — no lista ni
    // inicia nada: trae ese resultado y lo muestra en la misma pantalla wow.
    if (resultDirecto) {
      examResult(resultDirecto)
        .then((d) => { if (vivo) { setResultado(d.result); setFase('resultado'); setCargando(false); } })
        .catch((e) => { if (vivo) { setError(e.message); setCargando(false); } });
      return () => { vivo = false; };
    }
    listExams()
      .then((d) => {
        if (!vivo) return;
        const lista = d.exams || [];
        setExams(lista);
        setCargando(false);
        if (codeDirecto) {
          const elegido = lista.find((e) => e.code === codeDirecto);
          if (elegido) empezar(elegido);
        }
      })
      .catch((e) => { if (vivo) { setError(e.message); setCargando(false); } });
    return () => { vivo = false; };
  }, [codeDirecto, resultDirecto, empezar]);

  const alTerminar = (result) => { setResultado(result); setSesion(null); setFase('resultado'); };
  // El curso del alumno vive en /home (los niveles + la tarjeta del examen).
  // NO en "/" — esa es la landing/diagnóstico y, estando logueado, se siente
  // como que te expulsa a la sesión.
  const alCurso = () => navigate('/home');
  const volverLista = () => {
    setResultado(null); setSesion(null); setFase('lista'); setCargando(true);
    listExams().then((d) => { setExams(d.exams || []); setCargando(false); }).catch(() => setCargando(false));
  };

  return (
    <div className="exn-scope">
      <div className="exn-aura" aria-hidden="true" />
      <div className="exn-wrap">
        {fase === 'lista' && (
          <ListaExamenes exams={exams} cargando={cargando} error={error}
                         onElegir={empezar} onCurso={alCurso} />
        )}
        {fase === 'corriendo' && sesion && (
          <CorreExamen sesion={sesion} onTerminar={alTerminar} onSalir={volverLista} />
        )}
        {fase === 'resultado' && resultado && (
          <ResultadoExamen
            resultado={resultado}
            onVolver={volverLista}
            onCurso={alCurso}
            /* Repetir es EMPEZAR: se reutiliza el mismo camino que la primera
               vez, con el código del examen que acaba de terminar. */
            /* ⚠️  El resultado trae `exam_code`, no `code`, y `empezar`
                   espera el objeto con `code` y `title`. Pasarle otra cosa
                   no falla: deja el botón sin hacer nada. */
            onRepetir={resultado?.exam_code
              ? () => empezar({ code: resultado.exam_code, title: resultado.title })
              : undefined}
          />
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   LISTA
   ───────────────────────────────────────────────────────────────────────── */
/**
 * Una tarjeta de examen, con el aviso de que aún no ha terminado el nivel.
 *
 * ── EL CANDADO AVISA, NO CIERRA ────────────────────────────────────────────
 * Rob, 27-09-2026: «si no ha completado todas sus prácticas sale con candado,
 * pero si se quiere medir, decirle: no has hecho las lecciones, pero si te
 * quieres medir, dale».
 *
 * Las dos mitades importan, y la segunda más. Presentarse sin haber visto el
 * temario saca una nota que no dice nada y desanima — por eso se avisa. Pero
 * el alumno decide: hay quien ya sabe inglés y sólo quiere medirse, y hay
 * quien necesita saber dónde está antes de empezar. Un candado que de verdad
 * cierra convierte «todavía no te conviene» en «no puedes», que es otra cosa.
 *
 * Así que el aviso se enseña, se dice cuánto le falta con un número, y debajo
 * va la puerta para entrar igual.
 */
function ExamenCard({ e, i, onElegir }) {
  const [abierto, setAbierto] = useState(false);
  const avance = e.avance;
  const avisar = avance && !avance.listo && avance.total > 0;

  if (!avisar || abierto) {
    return (
      <button className="exn-card" style={{ animationDelay: `${i * 60}ms` }}
              onClick={() => onElegir(e)}>
        <span className="exn-card-glow" aria-hidden="true" />
        <div className="exn-card-top">
          <span className="exn-nivel">{e.cefr_target}</span>
          <span className="exn-n">{e.n_reactivos} preguntas</span>
        </div>
        <div className="exn-card-title">{e.title}</div>
        {e.description && <div className="exn-card-desc">{e.description}</div>}
        <div className="exn-card-cta">Empezar <span className="exn-arrow">→</span></div>
      </button>
    );
  }

  return (
    <div className="exn-card exn-card-aviso" style={{ animationDelay: `${i * 60}ms` }}
         data-prueba="examen-con-candado">
      <div className="exn-card-top">
        <span className="exn-nivel">{e.cefr_target}</span>
        <span className="exn-n">{e.n_reactivos} preguntas</span>
      </div>
      <div className="exn-card-title">
        <span className="exn-candado" aria-hidden="true">🔒</span> {e.title}
      </div>

      {/* El número, no un «aún no puedes»: saber que va por 3 de 34 es lo que
          le deja decidir.

          La unidad va PEGADA a la cifra («3 de 34 lecciones»), no sólo en la
          frase que la introduce. En esta tarjeta conviven dos números de cosas
          distintas —las preguntas del examen, arriba, y las lecciones del
          nivel, aquí— y sin la unidad al lado parecen la misma cuenta: «32
          preguntas» y «0 de 19» leídos juntos dan a entender que faltan
          preguntas por contestar. Lo confundió el primero que lo leyó, y quien
          lo lee mal decide mal. */}
      <div className="exn-card-desc">
        Todavía no has terminado las lecciones del nivel {e.cefr_target}:
        llevas <b>{avance.hechas} de {avance.total} lecciones</b>
        {avance.porcentaje > 0 && <> ({avance.porcentaje}%)</>}.
      </div>

      <div className="exn-avance-barra"
           role="img"
           aria-label={`${avance.hechas} de ${avance.total} lecciones del nivel `
                       + `${e.cefr_target} completadas`}>
        <div className="exn-avance-fill" style={{ width: `${avance.porcentaje}%` }} />
      </div>

      <div className="exn-card-desc">
        Si lo presentas ahora, la nota va a decirte más de lo que te falta por
        ver que de lo que sabes.
      </div>

      {/* Y la puerta, que es la mitad que de verdad pidió Rob. */}
      <button type="button" className="exn-igual"
              data-prueba="examen-medirme-igual"
              onClick={() => { setAbierto(true); onElegir(e); }}>
        Quiero medirme igual →
      </button>
    </div>
  );
}

function ListaExamenes({ exams, cargando, error, onElegir, onCurso }) {
  return (
    <div className="exn-screen">
      <button className="exn-volver-top" onClick={onCurso}>← Volver al curso</button>
      <header className="exn-head">
        <div className="exn-head-badge">🎓</div>
        <h1>Exámenes de nivel</h1>
        <p>Mide tu inglés en un examen concreto. Se califica al terminar.</p>
      </header>

      {error && <div className="exn-error">{error}</div>}
      {cargando ? (
        <div className="exn-vacio"><span className="exn-spin" />Cargando…</div>
      ) : !exams.length ? (
        <div className="exn-vacio">
          <p>Aún no hay exámenes disponibles.</p>
          <button className="exn-btn" onClick={onCurso}>Volver al curso</button>
        </div>
      ) : (
        <div className="exn-grid">
          {exams.map((e, i) => (
<ExamenCard key={e.code} e={e} i={i} onElegir={onElegir} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   CORRIENDO — un reactivo a la vez, con ItemRenderer dentro de la escena
   ───────────────────────────────────────────────────────────────────────── */
function CorreExamen({ sesion, onTerminar, onSalir }) {
  const [item, setItem] = useState(sesion.item);
  const [progress, setProgress] = useState(sesion.progress);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(null);
  const startedAt = useRef(Date.now());

  useEffect(() => { startedAt.current = Date.now(); setError(null); }, [item?.item_id]);
  useEffect(() => () => cancelSpeech(), []);

  const send = useCallback(async (response) => {
    if (sending || !item) return;
    setSending(true); setError(null); cancelSpeech();
    try {
      const d = await answerExam({
        sessionId: sesion.sessionId, itemId: item.item_id,
        response, timeMs: Date.now() - startedAt.current,
      });
      setPending(null);
      if (d.done) { onTerminar(d.result); return; }
      setProgress(d.progress);
      setItem(d.item);
    } catch (e) { setPending(response); setError(e.message || 'Se cayó la conexión.'); }
    finally { setSending(false); }
  }, [sending, item, sesion.sessionId, onTerminar]);

  const salir = () => {
    if (window.confirm('¿Salir del examen? Tu avance queda guardado y puedes retomarlo.')) onSalir();
  };

  if (!item) return null;
  const total = progress?.total || 0;
  const hechos = progress?.respondidos || 0;
  const pct = total ? (hechos / total) * 100 : 0;

  return (
    <div className="exn-screen exn-run">
      <div className="exn-runbar">
        <button className="exn-x" onClick={salir} aria-label="Salir del examen">✕</button>
        <span className="exn-skill">
          <span aria-hidden="true">{SKILL_ICON[item.skill] || '•'}</span> {item.skill_label}
        </span>
        <span className="exn-count">{Math.min(hechos + 1, total)}<span>/{total}</span></span>
      </div>

      <div className="exn-barra"><div className="exn-barra-fill" style={{ width: `${pct}%` }} /></div>

      <div className="exn-stage" key={item.item_id}>
        <div className="exn-stage-inner as-scope">
          <ItemRenderer item={item} onSubmit={send} disabled={sending} />
        </div>
      </div>

      {sending && <p className="exn-hint">Guardando…</p>}
      {error && (
        <div className="exn-run-error">
          <p>{error}</p>
          <button className="exn-btn" onClick={() => send(pending)} disabled={sending}>Reintentar</button>
          <p className="exn-hint">Tu respuesta no se perdió. Puedes salir y volver: el examen retoma aquí.</p>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   RESULTADO — el efecto wow: anillo animado + aura + desglose
   ───────────────────────────────────────────────────────────────────────── */
function ResultadoExamen({ resultado, onVolver, onCurso, onRepetir }) {
  // ── VOLVER A PRESENTARLO ────────────────────────────────────────────────
  //  Rob: «si quiero repetirlo, ¿dónde?». El backend ya lo permitía —`start`
  //  abre una sesión nueva cuando la anterior terminó— y faltaba la puerta,
  //  igual que faltaba en la evaluación de nivel.
  //
  //  Con confirmación, por el mismo motivo: el examen son unos minutos y no se
  //  empieza sin querer. Y aquí hay una razón más, que es la que cambia el
  //  texto del diálogo: un examen del curso SE PUEDE repetir a propósito —para
  //  subir la nota—, así que lo que hay que decir no es «vas a perder algo»
  //  sino qué pasa con el intento de hoy.
  const [confirmando, setConfirmando] = useState(false);

  const r = resultado;
  const aprobado = r.aprobado;
  const meta = aprobado === true ? 'ok' : aprobado === false ? 'no' : 'neutro';
  const objetivo = Math.round(r.overall_score || 0);

  // El anillo se anima de 0 al puntaje al montar.
  const [pintado, setPintado] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setPintado(objetivo), 120);
    return () => clearTimeout(t);
  }, [objetivo]);

  const R = 78, C = 2 * Math.PI * R;
  const offset = C - (pintado / 100) * C;

  return (
    <div className={`exn-screen exn-result exn-result-${meta}`}>
      <div className={`exn-result-aura exn-aura-${meta}`} aria-hidden="true" />

      <div className="exn-ring-wrap">
        <svg className="exn-ring" viewBox="0 0 180 180" width="180" height="180">
          <circle cx="90" cy="90" r={R} className="exn-ring-bg" />
          <circle cx="90" cy="90" r={R} className="exn-ring-fg"
                  strokeDasharray={C} strokeDashoffset={offset}
                  transform="rotate(-90 90 90)" />
        </svg>
        <div className="exn-ring-txt">
          <div className="exn-ring-num">{pintado}<span>%</span></div>
          <div className="exn-ring-sub">{r.correctas} / {r.calificadas} correctas</div>
        </div>
      </div>

      <div className={`exn-veredicto exn-veredicto-${meta}`}>
        {aprobado === true && '¡Aprobado! 🎉'}
        {aprobado === false && `No alcanzaste el ${r.pass_score}%`}
        {aprobado === null && 'Examen completado'}
      </div>
      <h2 className="exn-result-tit">{r.title} · nivel {r.cefr_target}</h2>

      <div className="exn-skills">
        {r.por_destreza.map((s, i) => {
          const val = s.score != null ? Math.round(s.score) : null;
          return (
            <div key={s.skill} className="exn-skill-row" style={{ animationDelay: `${i * 70}ms` }}>
              <span className="exn-skill-ico" aria-hidden="true">{SKILL_ICON[s.skill] || '•'}</span>
              <span className="exn-skill-lbl">{s.label}</span>
              <span className="exn-skill-bar">
                <span className="exn-skill-bar-fill" style={{ width: `${val ?? 0}%` }} />
              </span>
              <span className="exn-skill-score">
                {val != null ? `${val}%` : (s.pendientes_ia ? '⏳' : '—')}
              </span>
            </div>
          );
        })}
      </div>

      {r.pendientes_produccion > 0 && (
        <p className="exn-nota">
          {r.pendientes_produccion} tarea(s) de expresión (hablar/escribir) quedan pendientes de revisión.
        </p>
      )}

      {/* ── QUÉ ES ESTA NOTA, Y QUÉ NO ES ───────────────────────────────
          Sin esta línea, alguien que saca un 80 en «Examen B2» se va
          convencido de que lleva un B2 acreditado, y puede ponerlo en un
          currículum. No lo lleva: esto mide su entrenamiento aquí y le dice
          dónde falla, que es para lo que existe. Decirlo cuesta una frase;
          no decirlo lo descubre el alumno en el peor momento. */}
      <p className="exn-alcance" data-prueba="examen-alcance">
        Esto es un <b>diagnóstico de tu entrenamiento</b>: te dice qué dominas y
        qué se te rompe, para saber en qué entrenar. No es una certificación
        oficial ni sustituye a un examen como el de Cambridge.
      </p>

      <div className="exn-result-acc">
        <button className="exn-btn exn-btn-primario" onClick={onCurso}>Volver al curso</button>
        <button className="exn-btn" onClick={onVolver}>Ver más exámenes</button>
        {onRepetir && (
          <button type="button" className="exn-repetir"
                  data-prueba="examen-repetir"
                  onClick={() => setConfirmando(true)}>
            Volver a presentarlo
          </button>
        )}
      </div>

      {confirmando && (
        <div className="exn-sobre" role="dialog" aria-modal="true"
             aria-labelledby="exn-confirmar-tit" data-prueba="examen-confirmar">
          <div className="exn-dialogo">
            <h3 className="exn-dialogo-tit" id="exn-confirmar-tit">
              ¿Volver a presentarlo?
            </h3>
            <p className="exn-dialogo-txt">
              Empiezas otra vez desde la primera pregunta y son unos minutos.
            </p>
            <p className="exn-dialogo-txt">
              Este intento se queda guardado: vas a ver los dos en tu historial,
              y así sabrás si mejoraste.
            </p>
            <div className="exn-dialogo-acc">
              <button type="button" className="exn-btn exn-btn-primario"
                      data-prueba="examen-repetir-si"
                      onClick={() => { setConfirmando(false); onRepetir(); }}>
                Sí, empezar de nuevo
              </button>
              <button type="button" className="exn-repetir"
                      data-prueba="examen-repetir-no"
                      onClick={() => setConfirmando(false)}>
                Mejor no
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
